import React, { useEffect, useState } from 'react';
import { Table, Button, Popconfirm, message, Tabs, Tag, Input, Modal, Form, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';

const STATUS_MAP = {
  published: { color: 'green', label: 'เผยแพร่แล้ว' },
  pending: { color: 'gold', label: 'รออนุมัติ' },
  rejected: { color: 'red', label: 'ไม่ผ่านอนุมัติ' },
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

// ===== Component จัดการหมวดหมู่ (เฉพาะระดับ ภาค) =====
const CategoryManage = () => {
  const [cats, setCats] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchCats = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/news-categories`);
      setCats(res.data || []);
    } catch {
      setCats([]);
    }
  };

  useEffect(() => {
    fetchCats();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (cat) => {
    setEditing(cat);
    form.setFieldsValue({ name: cat.name, description: cat.description });
    setModalOpen(true);
  };

  const onSave = async (values) => {
    try {
      if (editing) {
        await axios.put(`${API_BASE_URL}/api/news-categories/${editing.id}`, values, { headers: authHeaders() });
      } else {
        await axios.post(`${API_BASE_URL}/api/news-categories`, values, { headers: authHeaders() });
      }
      message.success('บันทึกหมวดหมู่เรียบร้อย');
      setModalOpen(false);
      fetchCats();
    } catch {
      message.error('ไม่สามารถบันทึกหมวดหมู่ได้');
    }
  };

  const onDelete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/news-categories/${id}`, { headers: authHeaders() });
      message.success('ลบหมวดหมู่เรียบร้อย');
      fetchCats();
    } catch {
      message.error('ไม่สามารถลบหมวดหมู่ได้ (อาจมีข่าวที่ใช้หมวดหมู่นี้อยู่)');
    }
  };

  return (
    <>
      <Button type="primary" icon={<PlusOutlined />} onClick={openCreate} style={{ marginBottom: 16 }}>
        เพิ่มหมวดหมู่
      </Button>
      <Table
        dataSource={cats}
        rowKey="id"
        pagination={false}
        scroll={{ x: 'max-content' }}
        columns={[
          { title: 'ชื่อหมวดหมู่', dataIndex: 'name', key: 'name' },
          { title: 'คำอธิบาย', dataIndex: 'description', key: 'description' },
          {
            title: 'การจัดการ',
            key: 'action',
            width: 180,
            render: (_, record) => (
              <Space>
                <Button size="small" onClick={() => openEdit(record)}>แก้ไข</Button>
                <Popconfirm title="ลบหมวดหมู่นี้?" onConfirm={() => onDelete(record.id)}>
                  <Button size="small" danger>ลบ</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />
      <Modal
        open={modalOpen}
        title={editing ? 'แก้ไขหมวดหมู่' : 'เพิ่มหมวดหมู่'}
        onCancel={() => setModalOpen(false)}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave}>
          <Form.Item name="name" label="ชื่อหมวดหมู่" rules={[{ required: true, message: 'กรุณากรอกชื่อหมวดหมู่' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="description" label="คำอธิบาย">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ===== หน้าจัดการข่าว =====
const NewsList = () => {
  const [newsList, setNewsList] = useState([]);
  const [profile, setProfile] = useState(null);
  const navigate = useNavigate();

  const fetchNews = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/news/all`, { headers: authHeaders() });
      setNewsList(res.data || []);
    } catch {
      message.error('โหลดข่าวล้มเหลว');
    }
  };

  const fetchProfile = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/users/me`, { headers: authHeaders() });
      setProfile(res.data);
    } catch {
      setProfile(null);
    }
  };

  useEffect(() => {
    fetchNews();
    fetchProfile();
  }, []);

  const isAdmin = profile?.level === 'ภาค';

  const deleteNews = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/news/${id}`, { headers: authHeaders() });
      message.success('ลบข่าวเรียบร้อยแล้ว');
      fetchNews();
    } catch {
      message.error('ไม่สามารถลบข่าวได้');
    }
  };

  const approveNews = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/news/${id}/approve`, {}, { headers: authHeaders() });
      message.success('อนุมัติข่าวเรียบร้อยแล้ว');
      fetchNews();
    } catch {
      message.error('ไม่สามารถอนุมัติข่าวได้');
    }
  };

  const rejectNews = async (id) => {
    try {
      await axios.put(`${API_BASE_URL}/api/news/${id}/reject`, {}, { headers: authHeaders() });
      message.success('ปฏิเสธข่าวเรียบร้อยแล้ว');
      fetchNews();
    } catch {
      message.error('ไม่สามารถปฏิเสธข่าวได้');
    }
  };

  const columns = [
    { title: 'หัวข้อข่าว', dataIndex: 'title', key: 'title' },
    { title: 'หมวดหมู่', dataIndex: 'category_name', key: 'category_name' },
    {
      title: 'สถานะ',
      key: 'status',
      width: 140,
      render: (_, record) => {
        const s = STATUS_MAP[record.status] || { color: 'default', label: record.status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 340,
      render: (_, record) => (
        <Space size={4} wrap>
          <Button size="small" onClick={() => navigate(`/news-detail/${record.id}`)}>แก้ไข</Button>
          {isAdmin && record.status === 'pending' && (
            <>
              <Button
                size="small"
                type="primary"
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
                onClick={() => approveNews(record.id)}
              >
                อนุมัติ
              </Button>
              <Button size="small" danger onClick={() => rejectNews(record.id)}>
                ปฏิเสธ
              </Button>
            </>
          )}
          <Popconfirm title="คุณแน่ใจหรือไม่ที่จะลบ?" onConfirm={() => deleteNews(record.id)}>
            <Button size="small" danger>ลบ</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabs = [
    {
      key: 'news',
      label: 'จัดการข่าว',
      children: (
        <>
          <Button type="primary" onClick={() => navigate('/news-detail')} style={{ marginBottom: 16 }}>
            เขียนข่าวใหม่
          </Button>
          <Table columns={columns} dataSource={newsList} rowKey="id" scroll={{ x: 'max-content' }} />
        </>
      ),
    },
  ];

  // แท็บจัดการหมวดหมู่ — เฉพาะผู้ใช้ระดับ ภาค
  if (isAdmin) {
    tabs.push({
      key: 'categories',
      label: 'จัดการหมวดหมู่เนื้อหา',
      children: <CategoryManage />,
    });
  }

  return (
    <div>
      <Tabs items={tabs} />
    </div>
  );
};

export default NewsList;
